import { Box, makeStyles, LabelValue } from '@dxb-web-ui-framework/design-system';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import clsx from 'clsx';
import React, { FC } from 'react';
import { TFunction } from 'react-i18next';
import { amountMinFractionDigits } from '../../../../content-composer/src/utils';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { calculateExchangeRate } from '../../utils/utils';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(4),
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
      flexDirection: 'column',
    },
  },
  boxContainer: {
    border: `2px solid ${theme.palette.background.default}`,
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
  },
  requestDealer: {
    flex: 1,
  },
}));

export interface ExchangeRatesInfoProps {
  t: TFunction;
  accountCurrency?: string;
  exchangeCurrency?: string;
  rateCurrencyISO?: string;
  isMobile: boolean;
  exchangeAmountValue?: number;
  initialAmountValue?: number;
  shouldShowExchangeRates: boolean;
  exchangeRate?: number;
  isLoading: boolean;
  shouldRoundSecondUnit?: boolean;
  exchangeAmountCurrency?: string;
}

const ExchangeRatesInfo: FC<ExchangeRatesInfoProps> = ({
  t,
  accountCurrency,
  exchangeCurrency,
  rateCurrencyISO,
  isMobile,
  exchangeAmountValue,
  initialAmountValue,
  shouldShowExchangeRates,
  exchangeRate,
  isLoading,
  shouldRoundSecondUnit,
  exchangeAmountCurrency,
  children,
}) => {
  const language = useLanguage();
  const classes = useStyles();
  const { firstCurrency, firstAmount, secondAmount, secondCurrency } = calculateExchangeRate(
    accountCurrency ?? '-',
    exchangeCurrency ?? '-',
    rateCurrencyISO ?? '-',
    exchangeRate ?? 0,
  );

  const exchangeAmount = Boolean(exchangeAmountValue && accountCurrency)
    ? formatAmount(exchangeAmountValue as number, exchangeAmountCurrency as string, 2, language.cultureName)
    : '-';

  const firstUnit =
    Boolean(accountCurrency) && Boolean(exchangeCurrency) && Boolean(rateCurrencyISO) && Boolean(exchangeRate)
      ? formatAmount(firstAmount, firstCurrency, 2)
      : '-';

  const secondUnit =
    Boolean(accountCurrency) && Boolean(exchangeCurrency) && Boolean(rateCurrencyISO) && Boolean(exchangeRate)
      ? formatAmount(secondAmount, secondCurrency, 4, undefined, false, 'floor')
      : '-';

  return (
    <Box className={clsx({ [classes.root]: shouldShowExchangeRates })}>
      {shouldShowExchangeRates && (
        <>
          <Box p={2} className={classes.boxContainer}>
            <LabelValue
              label={t('fundsTransferPage.ExchangeRateInfo.ExchangeAmount')}
              value={exchangeAmount}
              valueAreaLabel={`${t('fundsTransferPage.ExchangeRateInfo.ExchangeAmount')} ${exchangeAmount}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize={isMobile ? 'small' : 'medium'}
              valueColor='primary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
          </Box>
          <Box
            p={2}
            display='flex'
            justifyContent='space-between'
            className={clsx(classes.boxContainer, { [classes.requestDealer]: children })}>
            <LabelValue
              label={t('fundsTransferPage.ExchangeRateInfo.ExchangeRate')}
              value={`${firstUnit} = ${secondUnit}`}
              valueAreaLabel={`${t('fundsTransferPage.ExchangeRateInfo.ExchangeRate')}`}
              labelColor='textPrimary'
              labelSize='small'
              valueSize={isMobile ? 'small' : 'medium'}
              valueColor='primary'
              labelAlignment='left'
              valueAlignment='left'
              hasSkeleton={isLoading}
            />
            {children}
          </Box>
        </>
      )}
    </Box>
  );
};

export { ExchangeRatesInfo };
